import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/ui/design-system/utils/cn';
import type { Column } from '../types';

/**
 * Shared chrome for the inline list widgets (tables + the posts grid): the
 * grid templates (name column + one or two metric columns), the column header
 * row, and the "Show N more" expander. The title row is `InlineTableHeader`.
 */

/** Name column + two right-aligned metric columns. */
export const TABLE_GRID =
    'grid grid-cols-[minmax(0,1fr)_96px_96px] items-center gap-x-2';

// The posts grid and single-metric tables (related terms) have one metric
// column; every other table has two.
const SINGLE_COLUMN_GRID =
    'grid grid-cols-[minmax(0,1fr)_96px] items-center gap-x-2';

/** The grid for a column count — shared by the header and body rows so the
 * two can never disagree on the track layout. */
export const gridClassFor = (columnCount: number): string =>
    columnCount === 1 ? SINGLE_COLUMN_GRID : TABLE_GRID;

export const ColumnHeaderRow = ({
    nameColumnHeader,
    columns,
}: {
    nameColumnHeader: string;
    columns: Column[];
}) => (
    <div
        className={`${gridClassFor(
            columns.length,
        )} px-3.5 py-1.5 bg-background-low-em border-b border-med-em text-xs text-muted-foreground`}
    >
        <span className="capitalize">{nameColumnHeader}</span>
        {columns.map((col) => (
            // Block + right-aligned so long labels like "Views: Latest Week" wrap
            // to two lines instead of overflowing into the next column. The sort
            // column is distinguished by weight/color (per Figma), not an arrow.
            <span
                key={col.key}
                className={cn(
                    'block text-right leading-tight',
                    col.isSort && 'font-semibold text-foreground',
                )}
            >
                {col.label}
            </span>
        ))}
    </div>
);

/** Expand/collapse footer for capped lists ("Show N more" / "Show less"). */
export const ShowMoreButton = ({
    expanded,
    hiddenCount,
    controlsId,
    onToggle,
}: {
    expanded: boolean;
    hiddenCount: number;
    controlsId: string;
    onToggle: () => void;
}) => (
    <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={controlsId}
        className="group w-full flex items-center justify-center px-3.5 py-2 border-t border-border text-xs font-medium text-muted-foreground hover:text-foreground cursor-pointer"
    >
        <span className="inline-flex items-center gap-1 h-8 px-3 rounded-md group-hover:bg-muted/50">
            {expanded ? (
                <>
                    Show less
                    <ChevronUp size={16} />
                </>
            ) : (
                <>
                    Show {hiddenCount} more
                    <ChevronDown size={16} />
                </>
            )}
        </span>
    </button>
);
